/**
 * AppContext - Main Application Context
 *
 * Manages application state for:
 * - Current app (main vs admin)
 * - Organization selection (for admin app)
 * - App switching and navigation
 *
 * Replaces the demo ViewProvider with a production-ready context
 * that integrates with auth and is prepared for API integration.
 */

import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import type { Organization } from "@/services/organization";
import { useAuth } from "./AuthProvider";
import {
	type AppType,
	findOrganization,
	getDefaultOrganization,
	getStoredApp,
	getStoredOrgId,
	hasMultipleOrganizations,
	storeApp,
	storeOrgId,
	TEMP_ORGANIZATIONS,
} from "./app-context-storage";

// ============================================================================
// CONTEXT TYPE DEFINITIONS
// ============================================================================

interface AppContextType {
	// Current State
	currentApp: AppType;
	currentOrganization: Organization | null;
	availableOrganizations: Organization[];

	// Permissions (derived from auth)
	canAccessAdmin: boolean;
	canAccessMultipleOrgs: boolean;

	// Actions
	switchToApp: (app: AppType) => void;
	selectOrganization: (orgId: string) => void;

	// Utilities
	isMainApp: boolean;
	isAdminApp: boolean;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const AppContext = createContext<AppContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface AppContextProviderProps {
	children: ReactNode;
}

export function AppContextProvider({ children }: AppContextProviderProps) {
	const auth = useAuth();

	// ============================================================================
	// STATE INITIALIZATION
	// ============================================================================

	/**
	 * Initialize current app from:
	 * 1. LocalStorage (user preference)
	 * 2. Default to 'main'
	 */
	const [currentApp, setCurrentApp] = useState<AppType>(() => {
		const stored = getStoredApp();
		return stored || "main";
	});

	/**
	 * Initialize current organization from:
	 * 1. LocalStorage (last selected)
	 * 2. First available organization
	 * 3. null if no organizations
	 */
	const [currentOrganization, setCurrentOrganization] =
		useState<Organization | null>(() => {
			const storedId = getStoredOrgId();
			if (storedId) {
				const org = findOrganization(storedId);
				if (org) return org;
			}

			// Default to first organization if in admin app
			const app = getStoredApp();
			if (app === "admin" && TEMP_ORGANIZATIONS.length > 0) {
				return getDefaultOrganization();
			}

			return null;
		});

	// ============================================================================
	// DERIVED STATE (Permissions from Auth)
	// ============================================================================

	/**
	 * Check if user can access admin app
	 * Based on auth permissions
	 *
	 * TODO: Update this logic based on your CASL permissions
	 * Currently allows if authenticated - adjust as needed
	 */
	const canAccessAdmin = useMemo(() => {
		if (!auth.isAuthenticated || !auth.user) return false;

		// TODO: Replace with actual permission check
		// Example: return auth.ability.can('access', 'AdminApp');
		// For now, allow all authenticated users
		return true;
	}, [auth.isAuthenticated, auth.user]);

	/**
	 * Check if user can access multiple organizations
	 * Based on available organizations
	 */
	const canAccessMultipleOrgs = useMemo(() => {
		return hasMultipleOrganizations();
	}, []);

	/**
	 * Get available organizations for current user
	 *
	 * TODO: Replace with API call to fetch user's organizations
	 * Should use auth.user.id to fetch orgs from API
	 */
	const availableOrganizations = useMemo(() => {
		// Future: Fetch from API based on auth.user
		// For now, return temporary data
		return TEMP_ORGANIZATIONS;
	}, []);

	// ============================================================================
	// PERSISTENCE EFFECTS
	// ============================================================================

	/**
	 * Persist current app to localStorage
	 */
	useEffect(() => {
		storeApp(currentApp);
	}, [currentApp]);

	/**
	 * Persist current organization to localStorage
	 */
	useEffect(() => {
		if (currentOrganization) {
			storeOrgId(currentOrganization.id);
		}
	}, [currentOrganization]);

	// ============================================================================
	// ACTIONS
	// ============================================================================

	/**
	 * Switch to a different app (main or admin)
	 *
	 * NOTE: This only updates state. Components should handle navigation
	 * using the useAppNavigation hook.
	 */
	const switchToApp = useCallback(
		(app: AppType) => {
			// Check permissions for admin app
			if (app === "admin" && !canAccessAdmin) {
				console.warn("User does not have permission to access admin app");
				return;
			}

			setCurrentApp(app);

			// If switching to admin and no org selected, select first
			if (
				app === "admin" &&
				!currentOrganization &&
				availableOrganizations.length > 0
			) {
				setCurrentOrganization(getDefaultOrganization());
			}

			// Navigation is handled by components via useAppNavigation hook
		},
		[canAccessAdmin, currentOrganization, availableOrganizations.length],
	);

	/**
	 * Select an organization (for admin app)
	 *
	 * NOTE: This only updates state. Components should handle navigation
	 * using the useAppNavigation hook.
	 */
	const selectOrganization = useCallback(
		(orgId: string) => {
			const org = findOrganization(orgId);
			if (!org) {
				console.warn(`Organization with id "${orgId}" not found`);
				return;
			}

			setCurrentOrganization(org);

			// If not in admin app, switch to admin (state only)
			if (currentApp !== "admin") {
				setCurrentApp("admin");
			}

			// Navigation is handled by components via useAppNavigation hook
		},
		[currentApp],
	);

	// ============================================================================
	// CONTEXT VALUE
	// ============================================================================

	const value: AppContextType = {
		// State
		currentApp,
		currentOrganization,
		availableOrganizations,

		// Permissions
		canAccessAdmin,
		canAccessMultipleOrgs,

		// Actions
		switchToApp,
		selectOrganization,

		// Utilities
		isMainApp: currentApp === "main",
		isAdminApp: currentApp === "admin",
	};

	return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access AppContext
 * Must be used within AppContextProvider
 */
export function useAppContext() {
	const context = useContext(AppContext);
	if (context === undefined) {
		throw new Error("useAppContext must be used within AppContextProvider");
	}
	return context;
}

// ============================================================================
// EXPORTS
// ============================================================================

export type { AppContextType, Organization, AppType };
